import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCategories } from "@/contexts/CategoriesContext";
import { fmtCurrency } from "@/lib/format";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Gauge, Plus, Pencil, Trash2, AlertTriangle, LayoutGrid, List as ListIcon } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { iconForCategoryName } from "@/lib/categoryIcons";

export default function Budgets() {
    const { user } = useAuth();
    const cur = user?.currency || "USD";
    const { categories } = useCategories();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("list");
    const [editing, setEditing] = useState(null);
    const [deleteId, setDeleteId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/budgets");
            setItems(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const onDelete = async () => {
        if (!deleteId) return;
        try {
            await api.delete(`/budgets/${deleteId}`);
            toast.success("Budget removed");
            setItems(items.filter(i => i.id !== deleteId));
        } catch { toast.error("Failed to delete"); }
        finally { setDeleteId(null); }
    };

    const totalLimit = items.reduce((a, b) => a + parseFloat(b.limit_amount), 0);
    const totalSpent = items.reduce((a, b) => a + parseFloat(b.spent), 0);

    return (
        <div className="space-y-4" data-testid="budgets-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Budget Flow</h1>
                    <p className="text-sm text-slate-500 mt-1">Set spending limits per category and track them in real time.</p>
                </div>
                <div className="flex items-center gap-2">
                    {/* Cards/list toggle only matters at lg+ — mobile always uses the compact stacked list below */}
                    <Tabs value={view} onValueChange={setView} className="hidden lg:block">
                        <TabsList className="bg-slate-100 rounded-xl p-1">
                            <TabsTrigger data-testid="budgets-view-cards" value="cards" className="rounded-lg data-[state=active]:bg-white px-2.5">
                                <LayoutGrid className="w-4 h-4" />
                            </TabsTrigger>
                            <TabsTrigger data-testid="budgets-view-list" value="list" className="rounded-lg data-[state=active]:bg-white px-2.5">
                                <ListIcon className="w-4 h-4" />
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                    <Button onClick={() => setEditing({ _new: true, period: "monthly" })} data-testid="new-budget-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl gap-1.5">
                        <Plus className="w-4 h-4" /> New budget
                    </Button>
                </div>
            </div>

            {items.length > 0 && (
                <div className="card-elev rounded-2xl p-5 flex items-center justify-between" data-testid="budgets-summary">
                    <div>
                        <p className="text-xs text-slate-500 mb-0.5">Total spent across budgets</p>
                        <p className="font-display text-2xl font-extrabold text-slate-900 tabular-nums">{fmtCurrency(totalSpent, cur)}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-xs text-slate-500 mb-0.5">Total limit</p>
                        <p className="font-display text-lg font-bold text-slate-600 tabular-nums">{fmtCurrency(totalLimit, cur)}</p>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="card-elev rounded-2xl p-10 text-center text-slate-400 text-sm">Loading…</div>
            ) : items.length === 0 ? (
                <div className="card-elev rounded-2xl">
                    <EmptyState icon={Gauge} title="No budgets yet" description="Set a weekly or monthly limit for a category — Groceries, Entertainment, Shopping, anything — and track it here."
                        action={<Button onClick={() => setEditing({ _new: true, period: "monthly" })} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"><Plus className="w-4 h-4 mr-1.5" /> New budget</Button>}
                    />
                </div>
            ) : (
                <>
                    {/* Mobile: compact stacked list, no horizontal scroll — always shown below lg regardless of the cards/list toggle */}
                    <div className="lg:hidden card-elev rounded-2xl divide-y divide-slate-100 overflow-hidden" data-testid="budgets-mobile-list">
                        {items.map((b, i) => {
                            const limit = parseFloat(b.limit_amount) || 0;
                            const spent = parseFloat(b.spent) || 0;
                            const remaining = parseFloat(b.remaining);
                            const pct = Math.min(100, b.percent || 0);
                            const over = spent > limit;
                            const nearLimit = !over && b.percent >= 80;
                            const { icon: BudgetMobileIcon, color: iconColor } = iconForCategoryName(categories, b.category);
                            const color = over ? "#EF4444" : nearLimit ? "#F59E0B" : iconColor;
                            return (
                                <div key={b.id} data-testid={`m-budget-${b.id}`} className="flex items-center gap-2.5 px-3 py-2.5">
                                    <span className="w-8 h-8 rounded-lg grid place-items-center flex-shrink-0" style={{ backgroundColor: `${iconColor}18`, color: iconColor }}>
                                        <BudgetMobileIcon className="w-4 h-4" />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="font-semibold text-sm text-slate-900 truncate">{b.category}</span>
                                            <span className="font-display font-bold text-xs text-slate-900 tabular-nums flex-shrink-0">
                                                {fmtCurrency(spent, cur)}<span className="text-slate-400 font-normal">/{fmtCurrency(limit, cur)}</span>
                                            </span>
                                        </div>
                                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                                            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                                        </div>
                                        <div className="flex items-center justify-between mt-1 text-[11px]">
                                            {over ? (
                                                <span className="text-rose-600 font-semibold">Over by {fmtCurrency(Math.abs(remaining), cur)}</span>
                                            ) : (
                                                <span className={nearLimit ? "text-amber-600 font-semibold" : "text-slate-500"}>{fmtCurrency(remaining, cur)} left</span>
                                            )}
                                            <span className="text-slate-400 capitalize flex-shrink-0">{b.period} · {Math.round(b.percent)}%</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-0.5 flex-shrink-0">
                                        <button onClick={() => setEditing(b)} data-testid={`m-budget-edit-${b.id}`} className="p-1 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={() => setDeleteId(b.id)} data-testid={`m-budget-delete-${b.id}`} className="p-1 rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Desktop (lg+): unchanged cards/list toggle */}
                    <div className="hidden lg:block">
                    {view === "cards" ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="budgets-grid">
                    {items.map((b, i) => {
                        const limit = parseFloat(b.limit_amount) || 0;
                        const spent = parseFloat(b.spent) || 0;
                        const remaining = parseFloat(b.remaining);
                        const pct = Math.min(100, b.percent || 0);
                        const over = spent > limit;
                        const nearLimit = !over && b.percent >= 80;
                        const { icon: BudgetCardIcon, color: iconColor } = iconForCategoryName(categories, b.category);
                        const color = over ? "#EF4444" : nearLimit ? "#F59E0B" : iconColor;
                        return (
                            <div key={b.id} data-testid={`budget-${b.id}`} className="card-elev rounded-2xl p-5 group">
                                <div className="flex items-start justify-between mb-3">
                                    <div className="min-w-0 flex items-center gap-2.5">
                                        <span className="w-8 h-8 rounded-lg grid place-items-center flex-shrink-0" style={{ backgroundColor: `${iconColor}18`, color: iconColor }}>
                                            <BudgetCardIcon className="w-4 h-4" />
                                        </span>
                                        <div className="min-w-0">
                                            <h3 className="font-display text-base font-bold text-slate-900 truncate">{b.category}</h3>
                                            <p className="text-xs text-slate-500 mt-0.5 capitalize">{b.period} budget · resets {b.days_left <= 0 ? "today" : `in ${b.days_left}d`}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                        <button onClick={() => setEditing(b)} data-testid={`budget-edit-${b.id}`} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"><Pencil className="w-4 h-4" /></button>
                                        <button onClick={() => setDeleteId(b.id)} data-testid={`budget-delete-${b.id}`} className="p-1.5 rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                </div>

                                <div className="flex items-end justify-between mb-1.5">
                                    <span className="font-display text-2xl font-extrabold text-slate-900 tabular-nums">{fmtCurrency(spent, cur)}</span>
                                    <span className="text-xs text-slate-500 mb-0.5">of {fmtCurrency(limit, cur)}</span>
                                </div>
                                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                                </div>
                                <div className="flex items-center justify-between mt-2 text-xs">
                                    {over ? (
                                        <span className="text-rose-600 font-semibold inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Over by {fmtCurrency(Math.abs(remaining), cur)}</span>
                                    ) : (
                                        <span className={nearLimit ? "text-amber-600 font-semibold" : "text-slate-500"}>{fmtCurrency(remaining, cur)} left</span>
                                    )}
                                    <span className="font-semibold text-slate-600 tabular-nums">{Math.round(b.percent)}%</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="card-elev rounded-2xl overflow-hidden" data-testid="budgets-list">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50/70 text-slate-500 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="text-left font-semibold px-5 py-3">Category</th>
                                    <th className="text-left font-semibold px-5 py-3 hidden md:table-cell">Period</th>
                                    <th className="text-left font-semibold px-5 py-3 min-w-[160px]">Progress</th>
                                    <th className="text-right font-semibold px-5 py-3">Spent</th>
                                    <th className="text-right font-semibold px-5 py-3 hidden md:table-cell">Limit</th>
                                    <th className="text-right font-semibold px-5 py-3 hidden lg:table-cell">Resets</th>
                                    <th className="text-right font-semibold px-5 py-3 w-24"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {items.map((b, i) => {
                                    const limit = parseFloat(b.limit_amount) || 0;
                                    const spent = parseFloat(b.spent) || 0;
                                    const remaining = parseFloat(b.remaining);
                                    const pct = Math.min(100, b.percent || 0);
                                    const over = spent > limit;
                                    const nearLimit = !over && b.percent >= 80;
                                    const { icon: BudgetRowIcon, color: iconColor } = iconForCategoryName(categories, b.category);
                                    const color = over ? "#EF4444" : nearLimit ? "#F59E0B" : iconColor;
                                    return (
                                        <tr key={b.id} data-testid={`budget-row-${b.id}`} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-5 py-3.5">
                                                <div className="flex items-center gap-2">
                                                    <span className="w-6 h-6 rounded-md grid place-items-center flex-shrink-0" style={{ backgroundColor: `${iconColor}18`, color: iconColor }}>
                                                        <BudgetRowIcon className="w-3.5 h-3.5" />
                                                    </span>
                                                    <span className="font-semibold text-slate-900">{b.category}</span>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5 text-slate-600 hidden md:table-cell capitalize">{b.period}</td>
                                            <td className="px-5 py-3.5">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                                                    </div>
                                                    <span className="text-xs font-semibold text-slate-600 tabular-nums w-10 text-right">{Math.round(b.percent)}%</span>
                                                </div>
                                                {over ? (
                                                    <span className="text-[11px] text-rose-600 font-semibold inline-flex items-center gap-1 mt-1"><AlertTriangle className="w-3 h-3" /> Over by {fmtCurrency(Math.abs(remaining), cur)}</span>
                                                ) : null}
                                            </td>
                                            <td className="px-5 py-3.5 text-right font-display font-bold text-slate-900 tabular-nums">{fmtCurrency(spent, cur)}</td>
                                            <td className="px-5 py-3.5 text-right text-slate-500 hidden md:table-cell tabular-nums">{fmtCurrency(limit, cur)}</td>
                                            <td className="px-5 py-3.5 text-right text-slate-500 hidden lg:table-cell">{b.days_left <= 0 ? "today" : `in ${b.days_left}d`}</td>
                                            <td className="px-5 py-3.5 text-right">
                                                <div className="inline-flex gap-1">
                                                    <button onClick={() => setEditing(b)} data-testid={`budget-row-edit-${b.id}`} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"><Pencil className="w-4 h-4" /></button>
                                                    <button onClick={() => setDeleteId(b.id)} data-testid={`budget-row-delete-${b.id}`} className="p-1.5 rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
                    )}
                    </div>
                </>
            )}

            <BudgetEditor item={editing} existing={items} onClose={() => setEditing(null)} onSaved={load} />

            <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this budget?</AlertDialogTitle>
                        <AlertDialogDescription>This only removes the limit — your logged expenses in this category are unaffected.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={onDelete} className="bg-rose-600 hover:bg-rose-700 text-white">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function BudgetEditor({ item, existing, onClose, onSaved }) {
    const { categories, categoryNames } = useCategories();
    const [form, setForm] = useState(item || {});
    const [saving, setSaving] = useState(false);
    useEffect(() => { setForm(item || {}); }, [item]);
    if (!item) return null;

    const budgetedCategories = new Set(existing.filter(e => e.id !== item.id).map(e => e.category));
    const availableCategories = categoryNames.filter(c => !budgetedCategories.has(c));

    const submit = async () => {
        if (!form.category) { toast.error("Choose a category"); return; }
        if (!form.limit_amount || parseFloat(form.limit_amount) <= 0) { toast.error("Enter a limit greater than 0"); return; }
        try {
            setSaving(true);
            const payload = {
                category: form.category, period: form.period || "monthly",
                limit_amount: parseFloat(form.limit_amount),
            };
            if (form._new) await api.post("/budgets", payload);
            else await api.patch(`/budgets/${form.id}`, payload);
            toast.success(form._new ? "Budget created" : "Budget updated");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-md rounded-2xl" data-testid="budget-editor">
                <DialogHeader><DialogTitle className="font-display text-xl">{form._new ? "New budget" : "Edit budget"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <FieldL label="Category">
                        <Select value={form.category || ""} onValueChange={v => setForm({ ...form, category: v })}>
                            <SelectTrigger data-testid="be-cat"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                            <SelectContent>
                                {availableCategories.map(c => {
                                    const { icon: CatIcon, color } = iconForCategoryName(categories, c);
                                    return <SelectItem key={c} value={c}><span className="inline-flex items-center gap-2"><CatIcon className="w-3.5 h-3.5" style={{ color }} />{c}</span></SelectItem>;
                                })}
                            </SelectContent>
                        </Select>
                        {availableCategories.length === 0 ? (
                            <p className="text-xs text-amber-600">Every category already has a budget.</p>
                        ) : null}
                    </FieldL>
                    <div className="grid grid-cols-2 gap-3">
                        <FieldL label="Period">
                            <Select value={form.period || "monthly"} onValueChange={v => setForm({ ...form, period: v })}>
                                <SelectTrigger data-testid="be-period"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="weekly">Weekly</SelectItem>
                                    <SelectItem value="monthly">Monthly</SelectItem>
                                </SelectContent>
                            </Select>
                        </FieldL>
                        <FieldL label="Limit amount">
                            <Input data-testid="be-limit" type="number" step="0.01" min="0" value={form.limit_amount || ""} onChange={e => setForm({ ...form, limit_amount: e.target.value })} placeholder="0.00" />
                        </FieldL>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button data-testid="be-save" disabled={saving} onClick={submit} className="bg-indigo-600 hover:bg-indigo-700 text-white">{form._new ? "Create" : "Save"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FieldL({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">{label}</Label>
            {children}
        </div>
    );
}
